class Equalize {
  static nodeListChildren(elements: HTMLElement[], querySelector: string): HTMLElement[] {
    const children: HTMLElement[] = [];

    elements.forEach((el: HTMLElement) => {
      children.push(...Array.from(el.querySelectorAll(querySelector)) as HTMLElement[]);
    });

    return children;
  }

  static nodeListSetStyleProperty(elements: HTMLElement[], property: string, value: string): void {
    elements.forEach((el) => {
      el.style.setProperty(property, value);
    });
  }

  static nodeListRemoveStyleProperty(elements: HTMLElement[], property: string): void {
    elements.forEach((el) => {
      el.style.removeProperty(property);
    });
  }

  static nodeListFilterByClass(elements: HTMLElement[], className: string): HTMLElement[] {
    return [...elements].filter((el) => el.classList.contains(className));
  }

  static nodeListFilterByMissingClass(elements: HTMLElement[], className: string): HTMLElement[] {
    return [...elements].filter((el) => !el.classList.contains(className));
  }

  static nodeListFilterByDataEqAttribute(
    elements: HTMLElement[],
    attribute: string,
    value: string | number | null = null,
  ): HTMLElement[] {
    if (value === null) {
      return [...elements].filter(
        (el) => Object.prototype.hasOwnProperty.call(el.dataset, attribute),
      );
    }

    return [...elements].filter((el) => (
      Object.prototype.hasOwnProperty.call(el.dataset, attribute)
      && el.dataset[attribute] === value
    ));
  }

  static getRowElements(top: number, elements: HTMLElement[]): HTMLElement[] {
    return [...elements].filter((el) => {
      const rect = el.getBoundingClientRect();

      return (rect.top >= top - 16 && rect.top <= top + 16);
    });
  }

  static elementIsVisible(element: HTMLElement): boolean {
    return element.classList.contains('io-eq--force') || !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }

  static equalize(rowElements: HTMLElement[]): void {
    const rowElementsEqParts = Equalize.nodeListChildren(rowElements, '[data-eq-part]');
    const rowElementsEqPartGroups = Equalize.nodeListChildren(rowElements, '[data-eq-part-group]');

    if (rowElementsEqParts.length) {
      const eqPartKeys: string[] = [];
      rowElementsEqParts.forEach((el) => {
        const { eqPart } = el.dataset;

        if (eqPart && eqPartKeys.indexOf(eqPart) === -1) {
          eqPartKeys.push(eqPart);
        }
      });

      const eqPartGroups: string[] = [];
      rowElementsEqPartGroups.forEach((el) => {
        const { eqPartGroup } = el.dataset;

        if (eqPartGroup && eqPartGroups.indexOf(eqPartGroup) === -1) {
          eqPartGroups.push(eqPartGroup);
        }
      });

      eqPartKeys.forEach((partKey: string) => {
        let minHeight = 0;
        const eqPartElements = Equalize.nodeListChildren(rowElements, `[data-eq-part*="${partKey}"]`);

        eqPartElements.forEach((el) => {
          const elHeight = el.clientHeight;
          if (elHeight > minHeight) {
            minHeight = elHeight;
          }
        });

        Equalize.nodeListSetStyleProperty(eqPartElements, 'min-height', `${minHeight}px`);
      });

      eqPartGroups.forEach((group) => {
        const groupKeys = group.split(',');
        let minHeight = 0;

        groupKeys.forEach((groupKey: string) => {
          const eqPartGroupElements = Equalize.nodeListChildren(rowElements, `[data-eq-part*="${groupKey}"]`);

          minHeight += eqPartGroupElements[0].clientHeight;
        });

        const eqPartGroupElements = Equalize.nodeListChildren(rowElements, `[data-eq-part-group*="${group}"]`);

        Equalize.nodeListSetStyleProperty(eqPartGroupElements, 'min-height', `${minHeight}px`);

        let groupMaxHeight = 0;

        eqPartGroupElements.forEach((el) => {
          if (el.clientHeight > groupMaxHeight && el.clientHeight > minHeight) {
            groupMaxHeight = el.clientHeight;
          }
        });

        if (groupMaxHeight > 0) {
          Equalize.nodeListSetStyleProperty(eqPartGroupElements, 'min-height', `${groupMaxHeight}px`);

          eqPartGroupElements.forEach((element) => {
            const elEqParts = Equalize.nodeListChildren([element], '[data-eq-part]').filter(
              (el) => el.dataset.eqPart && groupKeys.indexOf(el.dataset.eqPart) !== -1,
            );
            let partsHeight = 0;

            const elEqPartsFirst = elEqParts.filter((el) => el.dataset.eqPart === groupKeys[0]);
            if (elEqParts.length === groupKeys.length) {
              elEqParts.forEach((el) => {
                partsHeight += el.clientHeight;
              });

              if (partsHeight < groupMaxHeight) {
                elEqPartsFirst.forEach((el) => {
                  element.style.setProperty('min-height', `${el.clientHeight + (groupMaxHeight - partsHeight)}px`);
                });
              }
            }
          });
        }
      });
    } else {
      let minHeight = 0;
      rowElements.forEach((el) => {
        if (el.clientHeight > minHeight) {
          minHeight = el.clientHeight;
        }
      });
      Equalize.nodeListSetStyleProperty(rowElements, 'min-height', `${minHeight}px`);
    }

    rowElements.forEach((el) => {
      el.classList.add('eq-runned');
    });
  }

  static run(containerElement: HTMLElement | null = null): void {
    const containerToEqualize = containerElement || document;
    [...containerToEqualize.querySelectorAll('.io-eq')].filter(
      (el) => Equalize.elementIsVisible(el as HTMLElement),
    ).forEach((eqCont) => {
      const eqElements = [...eqCont.querySelectorAll('.io-eq-el')].filter(
        (el) => Equalize.elementIsVisible(el as HTMLElement),
      );
      eqElements.forEach((eqEl) => {
        eqEl.classList.remove('eq-runned');
      });

      const eqElementsChildren = Equalize.nodeListChildren(eqElements as HTMLElement[], '[data-eq-part]');

      if (!eqElementsChildren.length) {
        Equalize.nodeListRemoveStyleProperty(eqElements as HTMLElement[], 'min-height');
      } else {
        Equalize.nodeListRemoveStyleProperty(eqElementsChildren, 'min-height');
      }

      while (
        Equalize.nodeListFilterByClass(
          eqElements as HTMLElement[],
          'eq-runned',
        ).length < eqElements.length
      ) {
        const firstOfRow = Equalize.nodeListFilterByMissingClass(eqElements as HTMLElement[], 'eq-runned')[0];

        const rect = firstOfRow.getBoundingClientRect();
        const { top } = rect;

        const rowElements = Equalize.getRowElements(top, eqElements as HTMLElement[]);

        Equalize.equalize(rowElements);
      }
    });
  }
}

export default Equalize;
